import { useMemo } from 'react';
import cn from 'classnames';
import { Typography, WithInfoIcon } from 'seagull';
import { useIoContext } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { relationshipModel, serviceCostModel, timeModel, serviceModel } from "../../../models";
import { utils } from "../../../utils";
import { useResponsive } from "../../contexts";
import BillingSchedule from "../components/BillingSchedule";
import { BillingGroupTypes, summarize } from "../genServicesSummary";
import styles from "./ReviewServices.module.scss";
import { Service } from "./Service";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
export function ReviewServices(props) {
  const {
    relationship,
    selectedBundleID,
    selectedOptionalServices,
    setSelectedOptionalServices
  } = props;
  const {
    isMobile
  } = useResponsive();
  const {
    RM_BILLING_SUMMARY
  } = useFeatureFlags();
  const {
    getNow
  } = useIoContext();
  const effectiveDate = relationshipModel.effectiveDate(relationship);
  const [services, nonOptionalServices, optionalServices] = useMemo(() => {
    const services = relationshipModel.actualServices(relationship, selectedBundleID);
    return [services, services.filter(s => s.participation.type === 'mandatory'), services.filter(s => s.participation.type === 'optional')];
  }, [relationship, selectedBundleID]);
  const billingSummary = useMemo(() => services.reduce(summarize({
    selectedOptionalServices: selectedOptionalServices || [],
    effectiveDate,
    now: getNow(),
    billingFrequencyOnly: true
  }), {}), [services, selectedOptionalServices, effectiveDate]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.root,
    children: [!RM_BILLING_SUMMARY && services.length > 1 && !services.map(serviceModel.cost).some(cost => serviceCostModel.isVariablePrice(cost) || serviceCostModel.isRangePrice(cost)) ? /*#__PURE__*/_jsx("section", {
      "data-testid": "billing-summary",
      className: styles.section,
      children: [BillingGroupTypes.Once, BillingGroupTypes.Weekly, BillingGroupTypes.BiWeekly, BillingGroupTypes.Monthly, BillingGroupTypes.Quarterly, BillingGroupTypes.Yearly].flatMap(groupType => {
        return [false].map(variablePrice => {
          var _billingSummary$group;
          const group = (_billingSummary$group = billingSummary[groupType]) === null || _billingSummary$group === void 0 ? void 0 : _billingSummary$group.get(variablePrice);
          return group != null ? /*#__PURE__*/_jsx("div", {
            className: styles.billingSchedule,
            "data-testid": "billing-schedule-service-summary",
            children: /*#__PURE__*/_jsxs(Typography, {
              variant: "paragraph1",
              noWrap: true,
              className: cn(styles.coverLetter, {
                [styles.mobile]: isMobile
              }),
              children: [/*#__PURE__*/_jsxs(Typography, {
                as: "span",
                children: ["Billed ", groupType, groupType === BillingGroupTypes.OnEffectiveDate ? " (".concat(timeModel.formatDate(effectiveDate), ")") : '', ' ', "-", ' ']
              }), /*#__PURE__*/_jsx(Typography, {
                as: "span",
                weight: "bold",
                children: utils.formatDollarAmount(group.totalAmount)
              })]
            })
          }, "".concat(groupType, "-").concat(variablePrice)) : null;
        });
      })
    }) : null, /*#__PURE__*/_jsxs("section", {
      className: styles.section,
      children: [/*#__PURE__*/_jsx("div", {
        className: cn(styles.sectionTitle, {
          [styles.mobile]: isMobile
        }),
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          weight: "normal",
          children: "Services"
        })
      }), services.length > 0 ? /*#__PURE__*/_jsxs("ul", {
        children: [nonOptionalServices.map((service, i) => /*#__PURE__*/_jsx("li", {
          className: styles.serviceItem,
          children: /*#__PURE__*/_jsx(Service, {
            idx: i,
            relationship: relationship,
            service: service
          })
        }, i)), !!optionalServices.length && /*#__PURE__*/_jsx(WithInfoIcon, {
          as: "li",
          className: styles.serviceItem,
          info: "Services in this section are optional. Only the services selected will be added to your agreement",
          children: /*#__PURE__*/_jsx(Typography, {
            children: "Additional services to choose from"
          })
        }), optionalServices.map((service, i) => /*#__PURE__*/_jsx("li", {
          className: styles.serviceItem,
          children: /*#__PURE__*/_jsx(Service, {
            idx: i + nonOptionalServices.length,
            relationship: relationship,
            service: service,
            optionallySelected: selectedOptionalServices.includes(service.id),
            onChangeOptionallySelected: selected => {
              const newSelected = selected ? selectedOptionalServices.concat(service.id) : selectedOptionalServices.filter(id => id !== service.id);
              setSelectedOptionalServices(newSelected);
            }
          })
        }, i)), !RM_BILLING_SUMMARY && /*#__PURE__*/_jsx(BillingSchedule, {
          relationship: relationship,
          selectedBundleID: selectedBundleID,
          selectedOptionalServices: selectedOptionalServices
        })]
      }) : /*#__PURE__*/_jsx("div", {
        className: styles.agreementPreviewNoServices,
        children: /*#__PURE__*/_jsx(Typography, {
          skin: "secondary",
          children: "Once a package is selected, services will appear here"
        })
      })]
    })]
  });
}